import React from 'react';
import {
  Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { tableHeadRowSx, tableHeadCellSx, tableHeadTextSx, tableBodyCellSx } from '../../theme/tableStyles';
import { CreatableDescriptionSelect, DescriptionOption } from './CreatableDescriptionSelect';

export interface BiltyItemRow {
  invoiceNo?: string;
  invoiceDate?: string;
  description: string;
  quantity: number | string;
  actualWeight: number | string;
  chargedWeight: number | string;
  weight?: number | string;
  rate?: number | string;
  amount?: number | string;
}

interface BiltyItemsEditorProps {
  items: BiltyItemRow[];
  onChange: (items: BiltyItemRow[]) => void;
  descriptions?: DescriptionOption[];
  onCreateDescription?: (name: string) => Promise<DescriptionOption | null>;
}

const emptyRow = (): BiltyItemRow => ({
  invoiceNo: '',
  invoiceDate: new Date().toISOString().slice(0, 10),
  description: '',
  quantity: '',
  actualWeight: '',
  chargedWeight: '',
  rate: '',
  amount: '',
});

const headCell = () => ({ ...tableHeadCellSx(), py: 1, px: 1 });

const calcAmount = (row: BiltyItemRow): number => {
  const qty = Number(row.quantity) || 0;
  const charged = Number(row.chargedWeight ?? row.weight) || 0;
  const rate = Number(row.rate) || 0;
  if (!rate) return Number(row.amount) || 0;
  const fromQty = qty * rate;
  const fromWt = charged * rate;
  return Math.round((fromQty || fromWt) * 100) / 100;
};

export const BiltyItemsEditor: React.FC<BiltyItemsEditorProps> = ({
  items,
  onChange,
  descriptions = [],
  onCreateDescription,
}) => {
  const rows = items.length ? items : [emptyRow()];

  const updateRow = (index: number, patch: Partial<BiltyItemRow>) => {
    onChange(rows.map((row, i) => {
      if (i !== index) return row;
      const next = { ...row, ...patch };
      if ('quantity' in patch || 'chargedWeight' in patch || 'weight' in patch || 'rate' in patch) {
        next.amount = calcAmount(next);
      }
      return next;
    }));
  };

  const addRow = () => onChange([...rows, emptyRow()]);
  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  const totalActual = rows.reduce((sum, row) => sum + (Number(row.actualWeight) || 0), 0);
  const totalCharged = rows.reduce((sum, row) => sum + (Number(row.chargedWeight ?? row.weight) || 0), 0);
  const totalAmount = rows.reduce((sum, row) => sum + (Number(row.amount) || Number(calcAmount(row)) || 0), 0);

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${brandColors.border}`,
        borderRadius: `${RADIUS}px`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: brandCss.green50,
          borderBottom: `1px solid ${brandColors.border}`,
        }}
      >
        <Inventory2OutlinedIcon sx={{ color: brandCss.primary, fontSize: 20 }} />
        <Typography fontSize={13} fontWeight={800} color={brandCss.primaryDark}>
          Goods Description — Items
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow sx={tableHeadRowSx}>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Invoice No.</Typography></TableCell>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Invoice Date</Typography></TableCell>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Qty</Typography></TableCell>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Actual Wt (Kg)</Typography></TableCell>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Charged Wt (Kg)</Typography></TableCell>
              <TableCell sx={{ ...headCell(), minWidth: 220 }}><Typography sx={tableHeadTextSx}>Description</Typography></TableCell>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Rate</Typography></TableCell>
              <TableCell sx={headCell()}><Typography sx={tableHeadTextSx}>Amount</Typography></TableCell>
              <TableCell sx={{ ...headCell(), width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ ...tableBodyCellSx, py: 0.75, px: 1 }}>
                  <TextField size="small" fullWidth placeholder="Invoice no."
                    value={row.invoiceNo || ''} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { invoiceNo: e.target.value })} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField size="small" type="date" fullWidth
                    value={row.invoiceDate || ''} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { invoiceDate: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField size="small" type="number" fullWidth placeholder="0"
                    value={row.quantity} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { quantity: e.target.value })} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField size="small" type="number" fullWidth placeholder="0"
                    value={row.actualWeight ?? ''} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { actualWeight: e.target.value })} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField size="small" type="number" fullWidth placeholder="0"
                    value={row.chargedWeight ?? row.weight ?? ''} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { chargedWeight: e.target.value, weight: e.target.value })} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <CreatableDescriptionSelect
                    value={row.description}
                    options={descriptions}
                    onChange={(value) => updateRow(index, { description: value })}
                    onCreate={onCreateDescription}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField size="small" type="number" fullWidth placeholder="0"
                    value={row.rate ?? ''} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { rate: e.target.value })} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField size="small" type="number" fullWidth placeholder="0"
                    value={row.amount ?? calcAmount(row)} sx={catchyTextFieldSx}
                    onChange={(e) => updateRow(index, { amount: e.target.value })} />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 0.5 }}>
                  <IconButton size="small" onClick={() => removeRow(index)} disabled={rows.length === 1}
                    sx={{ color: brandColors.danger }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{
        px: 2, py: 1.25, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
        alignItems: 'center', gap: 1, borderTop: `1px solid ${brandColors.border}`, bgcolor: '#fafbfc',
      }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRow}
          sx={{ borderRadius: `${RADIUS}px`, fontWeight: 700, borderColor: brandCss.primary, color: brandCss.primary }}>
          Add Item
        </Button>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography fontSize={12} fontWeight={700} color={brandColors.textMuted}>
            Total Actual: <Box component="span" color={brandColors.textDark}>{totalActual.toFixed(2)} Kg</Box>
          </Typography>
          <Typography fontSize={12} fontWeight={700} color={brandColors.textMuted}>
            Total Charged: <Box component="span" color={brandColors.textDark}>{totalCharged.toFixed(2)} Kg</Box>
          </Typography>
          <Typography fontSize={12} fontWeight={700} color={brandColors.textMuted}>
            Items Total: <Box component="span" color={brandColors.textDark}>{totalAmount.toFixed(2)}</Box>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
